import React from "react";
import Link from "next/link";
import Head from "next/head";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import styles from "../styles/index.module.css";
import Share from "../components/Share";
import useToolsData from "../hooks/useToolsData";
import SeoSchema from "../components/SeoSchema";
import dynamic from "next/dynamic";

const GoogleAd = dynamic(() => import("../components/GoogleAd"), { ssr: false });

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
}

const Home = () => {
  const toolsData = useToolsData();
  const { t } = useTranslation();

  return (
    <>
      <SeoSchema
        title="iLovePDF | Best Free PDF Tools: Convert, Merge, Split, Compress, Edit Online"
        description="Work with PDF files online using iLovePDFTools: Convert PDF to Word, Excel, JPG. Merge, split, compress, and edit your documents for free in your browser."
        url="https://ilovepdftools.com"
        image="https://ilovepdftools.com/logo.png"
      />

      <Head>
        <title>Best Free PDF Tools – Convert, Merge, Compress, and Edit PDF Files Online</title>
        <meta
          name="description"
          content="Use iLovePDFTools to convert, merge, split, compress, and edit PDF files online for free. Access the fastest and most secure online PDF toolkit."
        />
        <meta
          name="keywords"
          content="best PDF tools, online PDF converter, convert PDF to Word, compress PDF, merge PDF, split PDF, free PDF editor, edit PDF online, PDF to JPG, PDF to Excel"
        />
        <link rel="canonical" href="https://ilovepdftools.com/" />

        <meta property="og:type" content="website" />
        <meta property="og:title" content="Best Free PDF Tools – Convert, Merge, Compress, and Edit PDF Files Online" />
        <meta
          property="og:description"
          content="Convert, merge, compress, and edit PDFs for free with iLovePDFTools. Access secure, high-speed online PDF editing from any browser."
        />
        <meta property="og:url" content="https://ilovepdftools.com/" />
        <meta property="og:image" content="https://ilovepdftools.com/logo.png" />
        <meta property="og:site_name" content="iLovePDFTools" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Best Free PDF Tools – Convert, Merge, Compress, and Edit PDF Files Online" />
        <meta
          name="twitter:description"
          content="Instantly convert, compress, split, and edit PDF files for free with iLovePDFTools. Simple and powerful online PDF toolkit."
        />
        <meta name="twitter:image" content="https://ilovepdftools.com/logo.png" />
        <meta name="twitter:site" content="@ilovepdftools" />
        <meta name="twitter:creator" content="@ilovepdftools" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "iLovePDFTools",
              "url": "https://ilovepdftools.com/",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://ilovepdftools.com/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
              },
              "description": "Free online PDF editor and converter. Merge, split, compress, and convert PDF to Word, Excel, JPG, and more.",
              "publisher": {
                "@type": "Organization",
                "name": "iLovePDFTools",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://ilovepdftools.com/logo.png"
                }
              }
            }),
          }}
        />
      </Head>

      <main>
        <header
          className="page_section header mb-0"
          style={{ minHeight: "180px" }}
        >
          <h1 className="title">
            Free Online PDF Tools – Merge, Split, Compress, Convert PDF to Word, JPG, Excel
          </h1>
          <p className="description">{t("common:page_header_text")}</p>

          <h2
            className="seo_heading"
            style={{ fontSize: "20px", fontWeight: "500", marginTop: "20px", marginBottom: "20px" }}
          >
            All-in-One PDF Converter and Editor Suite – 100% Free
          </h2>

          <p className="seo_text">
  iLovePDFTools is your complete suite of <strong>online PDF tools</strong> to handle all your document needs without downloads or signups.
</p>
<p className="seo_text">
  Quickly and securely <strong>merge PDF files</strong>, <strong>split large PDFs</strong>, and <strong>compress PDF size</strong> directly in your browser.
</p>
<p className="seo_text">
  You can also <strong>convert PDF to Word</strong>, <strong>Excel</strong>, or <strong>JPG</strong> with ease, and enjoy <strong>free PDF editing</strong> anytime, anywhere.
</p>

        </header>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "20px",
            margin: "20px 0",
            flexWrap: "wrap",
          }}
        >
          <div style={{ width: "160px", height: "600px" }}>
            <GoogleAd adSlot="7999075172" />
          </div>

          <div style={{ flex: "1 1 auto", maxWidth: "1000px" }}>
            <section style={{ marginBottom: "10px", marginTop: "10px" }}>
              <div className={styles.grid_container}>
                {Object.keys(toolsData).map((key) => (
                  <Link
                    key={key}
                    className={styles.grid_item}
                    href={toolsData[key].href}
                    prefetch={false}
                  >
                    <div className={styles.grid_content}>
                      <div className={styles.grid_item_icon}>{toolsData[key].icon}</div>
                      <p className={styles.grid_item_title}>{toolsData[key].title}</p>
                      <p className={styles.grid_item_description}>{toolsData[key].description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <div style={{ width: "160px", height: "600px" }}>
            <GoogleAd adSlot="5472165949" />
          </div>
        </div>

        <Share />
      </main>
    </>
  );
};

export default Home;
